import { FC, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import {
  PickupDeliveryLocationDeactivationReason,
  PickupDeliveryLocationResponseV1,
  PickupDeliveryLocationStatus,
} from '@forto-dev/customer-master-data-models'
import { AvatarIcon, CalendarIcon, Card, LabelValue, Separator, Stack } from '@forto-dev/phoenix-ui-core'

import { userQueryOptions } from '@/features/users'
import { useLocalisation } from '@/utils/localisation'

type LocationSearchOptionTooltipProps = {
  option: PickupDeliveryLocationResponseV1
}

enum ExternalUser {
  SERVICE = 'SERVICE',
  FORTO_USER = 'FORTO_USER',
}

const externalUserMap = {
  [ExternalUser.SERVICE]: 'Service',
  [ExternalUser.FORTO_USER]: 'Forto User',
}

export const LocationSearchOptionTooltip: FC<LocationSearchOptionTooltipProps> = (props) => {
  const { option } = props

  const { translateDate, translate, isValidTranslationKey } = useLocalisation()

  const { data: user, isLoading } = useQuery({
    ...userQueryOptions(option.updatedBy),
    enabled: option.updatedBy.startsWith('U-'),
  })

  const lastUpdatedByUserName = useMemo(() => {
    if (!option.updatedBy.startsWith('U-')) {
      return option.updatedBy === ExternalUser.SERVICE
        ? externalUserMap[ExternalUser.SERVICE]
        : externalUserMap[ExternalUser.FORTO_USER]
    }

    if (!user) {
      return ''
    }

    return `${user.firstName} ${user.lastName}`
  }, [option.updatedBy, user])

  const inactiveReason = isValidTranslationKey(`locations.inactiveReasons.${option.inactiveReason}`)
    ? translate(`locations.inactiveReasons.${option.inactiveReason as PickupDeliveryLocationDeactivationReason}`)
    : option.inactiveReason

  return (
    <Card>
      <Stack gap={16} fullWidth>
        <LabelValue label={translate('locations.notes')} value={option.notes ?? '-'} />
        {option.status === PickupDeliveryLocationStatus.INACTIVE && (
          <LabelValue label={translate('locations.inactiveReason')} value={inactiveReason} />
        )}
        <Separator orientation="horizontal" />

        <LabelValue
          prefix={<CalendarIcon size="lg" />}
          label={translate('locations.updatedAt')}
          value={translateDate('locations_location_updated_date', new Date(option.updatedAt))}
        />
        <LabelValue
          prefix={<AvatarIcon size="lg" />}
          label={translate('locations.updatedBy')}
          value={isLoading ? translate('locations.loading') : lastUpdatedByUserName}
        />
      </Stack>
    </Card>
  )
}
